export const JOURNEY_VARIANTS = {
  MOT: "mot",
  DUAL_REP: "dual_rep",
  SIGNATURE: "signature",
};

export const JOURNEY_VARIANT_SESSION_KEY = "mot_journey_variant";
export const DUAL_REP_PARAMS_SESSION_KEY = "mot_dual_rep_params";

/**
 * Determines the journey variant from the query string and persisted
 * session data.  A fresh load with the exact query flag `dualRep=true`
 * (case-insensitive key and value) selects DUAL_REP; any other value,
 * absence of the key, or a fresh load without it clears stale state
 * and returns MOT.  A refresh without query parameters restores the
 * stored variant (default MOT).
 *
 * Unavailable / malformed storage is caught; the variant derived from
 * the current query alone is returned instead of throwing.
 */
export function resolveJourneyVariant(search, storage) {
  const params = new URLSearchParams(search);

  const dualRepRaw = paramValueCI(params, "dualrep");
  const signatureRaw = paramValueCI(params, "signature");

  // Fresh load (query string exists)
  if (search) {
    if (signatureRaw !== null && signatureRaw.trim().toLowerCase() === "true") {
      try {
        storage.setItem(JOURNEY_VARIANT_SESSION_KEY, JOURNEY_VARIANTS.SIGNATURE);
      } catch (_) {}
      return JOURNEY_VARIANTS.SIGNATURE;
    }

    if (dualRepRaw !== null && dualRepRaw.trim().toLowerCase() === "true") {
      // Store variant and return DUAL_REP
      try {
        storage.setItem(JOURNEY_VARIANT_SESSION_KEY, JOURNEY_VARIANTS.DUAL_REP);
      } catch (_) {
        // storage unavailable — still return the correct variant
      }
      return JOURNEY_VARIANTS.DUAL_REP;
    }

    // Any other dualRep value (false, absent, etc.) — clear and return MOT
    try {
      storage.removeItem(JOURNEY_VARIANT_SESSION_KEY);
      storage.removeItem(DUAL_REP_PARAMS_SESSION_KEY);
    } catch (_) {
      // storage unavailable — keep going
    }
    return JOURNEY_VARIANTS.MOT;
  }

  // No query string — restore from storage
  try {
    const stored = storage.getItem(JOURNEY_VARIANT_SESSION_KEY);
    if (stored === JOURNEY_VARIANTS.DUAL_REP) {
      return JOURNEY_VARIANTS.DUAL_REP;
    }
  } catch (_) {
    // storage unavailable — default to MOT
  }
  return JOURNEY_VARIANTS.MOT;
}

/**
 * Reads a query parameter case-insensitively.
 * Returns the raw value string or null when the key is absent.
 */
function paramValueCI(params, key) {
  const lower = key.toLowerCase();
  for (const k of params.keys()) {
    if (k.toLowerCase() === lower) {
      return params.get(k);
    }
  }
  return null;
}

/**
 * SHA-256 hashes `JSON.stringify(customer)` into a lowercase 64-char
 * hex fingerprint.  Returns `null` (never throws) when Web Crypto is
 * unavailable.
 */
export async function buildSmsSubmissionFingerprint(customer) {
  try {
    if (!crypto || !crypto.subtle) return null;

    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(customer));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (_) {
    return null;
  }
}
